import React, { useEffect, useState } from "react";
import { Box, TextField, Button, Typography, IconButton, Container } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import logoImage from "../../assets/images/logo.png";
import callApi from "../../services/callApi";
import Loader from "../../components/Loader/Loader";
import ReferralBanner from "./components/ReferralBanner";

// ─── Estilos compartidos ─────────────────────────────────────────────────────

const cardSx = {
  width: "100%",
  p: { xs: "20px", md: "28px" },
  borderRadius: { xs: "16px", md: "20px" },
  background: "#ffffff",
  boxShadow: "0 2px 4px rgba(0,0,0,0.03), 0 8px 16px rgba(0,0,0,0.03), 0 12px 24px rgba(0,0,0,0.05)",
  border: "1px solid #eaeaea",
  boxSizing: "border-box",
};

const inputSx = {
  mb: 1,
  "& .MuiInputBase-root": {
    height: { xs: "44px", md: "46px" },
    backgroundColor: "#f7f9fc",
    fontFamily: "Quicksand-Regular",
    borderRadius: "10px",
    fontSize: { xs: "14px", md: "15px" },
  },
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    "& fieldset": { borderColor: "#e4e8ef" },
    "&:hover fieldset": { borderColor: "#a8b4c8" },
    "&.Mui-focused fieldset": { borderColor: "#1877f2", borderWidth: "1.5px" },
  },
  "& .MuiInputBase-input": { padding: "10px 14px", fontSize: { xs: "14px", md: "15px" } },
  "& .MuiInputLabel-root": {
    transform: "translate(14px, 11px) scale(1)",
    fontFamily: "Quicksand-Regular",
    fontSize: { xs: "14px", md: "15px" },
  },
  "& .MuiInputLabel-shrink": { transform: "translate(14px, -6px) scale(0.75)" },
  "& .MuiInputLabel-asterisk": { display: "none" },
};

const btnSx = {
  mt: 2,
  height: { xs: "46px", md: "48px" },
  borderRadius: "10px",
  bgcolor: "#1877f2",
  fontFamily: "Quicksand-Bold",
  fontSize: { xs: "15px", md: "16px" },
  textTransform: "none",
  boxShadow: "0 4px 12px rgba(24, 119, 242, 0.28)",
  transition: "all 0.2s ease",
  "&:hover": { bgcolor: "#166fe5", boxShadow: "0 6px 16px rgba(24, 119, 242, 0.38)" },
  "&.Mui-disabled": { bgcolor: "rgba(0,0,0,0.14)", color: "rgba(0,0,0,0.32)", boxShadow: "none" },
};

// ─── Paso 4: Información de Contacto ─────────────────────────────────────────

const ContactInfo = ({ userData, onNext, onBack }) => {
  const [telefono, setTelefono] = useState(userData?.telefono || "+56 ");
  const [email, setEmail]       = useState(userData?.email || "");
  const [password, setPassword] = useState(userData?.password || "");
  const [confirmPassword, setConfirmPassword] = useState(userData?.password || "");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError]   = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (userData?.telefono) setTelefono(userData.telefono);
    if (userData?.email)    setEmail(userData.email);
    if (userData?.password) { setPassword(userData.password); setConfirmPassword(userData.password); }
  }, [userData]);

  const handleTelefonoChange = (e) => {
    const prefix = "+56 ";
    let value = e.target.value;
    if (value.length < prefix.length) { setTelefono(prefix); return; }
    let nums = value.replace(/\D/g, "");
    if (nums.startsWith("56")) nums = nums.substring(2);
    nums = nums.substring(0, 9);
    let fmt = prefix;
    if (nums.length > 0) fmt += nums[0];
    if (nums.length > 1) fmt += " " + nums.substring(1, 5);
    if (nums.length > 5) fmt += " " + nums.substring(5, 9);
    setTelefono(fmt);
  };

  const isEmailValid    = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
  const isTelValid      = (t) => { const c = t.replace(/\D/g, ""); return c.length === 11 && c.startsWith("569"); };
  const isPassValid     = (p) => p.length >= 8;
  const isFormValid     = () => isTelValid(telefono) && isEmailValid(email) && isPassValid(password) && password === confirmPassword;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!telefono || !email || !password || !confirmPassword) { setError("Por favor, completa todos los campos"); return; }
    if (password !== confirmPassword) { setError("Las contraseñas no coinciden"); return; }
    setIsLoading(true);
    setError("");
    try {
      const tel = "+" + telefono.replace(/\D/g, "");
      const response = await callApi("/registro/enviarCodigo", "post", {
        email, telefono: tel, password,
        rut: userData.rut, nombres: userData.nombres,
        apellidos: userData.apellidos, profesion: userData.profesion,
      });
      if (response?.statusCode === 200) {
        onNext({ ...userData, telefono: tel, email, password, rut: userData.rut });
      } else {
        setError("El correo ya está registrado en el sistema");
      }
    } catch {
      setError("El correo ya está registrado en el sistema");
    } finally {
      setIsLoading(false);
    }
  };

  const EyeBtn = () => (
    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
    </IconButton>
  );

  return (
    <Box sx={{
      minHeight: "100vh",
      width: "100%",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "#ffffff",
      py: { xs: 3, md: 4 },
    }}>
      <Loader isLoading={isLoading} />
      <Container maxWidth="xs" sx={{ px: { xs: 2, md: 2 } }}>
        <Box sx={cardSx}>
          <Box sx={{ mb: { xs: 2, md: 2.5 }, display: "flex", justifyContent: "center" }}>
            <img src={logoImage} alt="Clinifica" style={{ height: "34px" }} />
          </Box>

          <Typography sx={{ fontFamily: "Poppins-SemiBold", fontSize: { xs: "18px", md: "20px" }, color: "#1c1c1c", mb: "6px" }}>
            Hola {userData?.nombres} 👋
          </Typography>
          <Typography sx={{ fontFamily: "Quicksand-Regular", fontSize: { xs: "13px", md: "14px" }, color: "#6b7280", mb: { xs: "20px", md: "24px" }, lineHeight: 1.5 }}>
            Necesitamos tus datos de contacto y una contraseña para crear tu cuenta
          </Typography>

          <form onSubmit={handleSubmit}>
            <TextField fullWidth label="Celular" value={telefono} onChange={handleTelefonoChange} margin="normal" required placeholder="+56 9 xxxx xxxx" sx={inputSx}
              error={telefono !== "+56 " && !isTelValid(telefono)}
              helperText={telefono !== "+56 " && !isTelValid(telefono) ? "Ingresa un número válido (+56 9 xxxx xxxx)" : ""}
            />
            <TextField fullWidth label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} margin="normal" required sx={inputSx}
              error={email !== "" && !isEmailValid(email)}
              helperText={email !== "" && !isEmailValid(email) ? "Ingresa un email válido" : ""}
            />
            <TextField fullWidth label="Contraseña" type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} margin="normal" required sx={inputSx}
              error={password !== "" && !isPassValid(password)}
              helperText={password !== "" && !isPassValid(password) ? "Mínimo 8 caracteres" : ""}
              InputProps={{ endAdornment: <EyeBtn /> }}
            />
            <TextField fullWidth label="Confirmar contraseña" type={showPassword ? "text" : "password"} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} margin="normal" required sx={inputSx}
              error={confirmPassword !== "" && password !== confirmPassword}
              helperText={confirmPassword !== "" && password !== confirmPassword ? "Las contraseñas no coinciden" : ""}
              InputProps={{ endAdornment: <EyeBtn /> }}
            />

            <Button type="submit" fullWidth variant="contained" disabled={!isFormValid()} sx={btnSx}>
              Continuar
            </Button>

            {error && (
              <Typography sx={{ color: "#d32f2f", fontSize: { xs: "12px", md: "13px" }, fontFamily: "Quicksand-Regular", textAlign: "center", mt: "12px", p: "12px", bgcolor: "rgba(211, 47, 47, 0.07)", borderRadius: "10px" }}>
                {error}
              </Typography>
            )}
          </form>

          <Button variant="text" onClick={onBack} sx={{ color: "#6b7280", textTransform: "none", fontFamily: "Quicksand-Regular", fontSize: { xs: "13px", md: "14px" }, mt: 1, p: 0 }}>
            volver
          </Button>
        </Box>

        <Box sx={{ mt: "12px" }}>
          <ReferralBanner />
        </Box>
      </Container>
    </Box>
  );
};

export default ContactInfo;
